import type { ReactNode } from 'react';
import { Alert, Chip, Stack, Text } from '@billda/design-system';
import { useFeature, useAnyFeature, useAllFeatures } from '@billda/features-ui';

interface FeatureSwitchGateProps {
  /** Single feature check */
  feature?: string;
  /** OR logic - at least one feature required */
  anyOf?: string[];
  /** AND logic - all features required */
  allOf?: string[];
  /** Content to render if feature is enabled */
  children: ReactNode;
  /** Optional fallback content if feature is disabled */
  fallback?: ReactNode;
  /** Show disabled message when feature is off (default: false) */
  showDisabledMessage?: boolean;
}

/**
 * DS fork of the features-ui package's FeatureSwitchGate — identical gate
 * semantics; the disabled message renders DS Alert/Chip instead of MUI.
 */
export const FeatureSwitchGate = ({
  feature,
  anyOf,
  allOf,
  children,
  fallback,
  showDisabledMessage = false,
}: FeatureSwitchGateProps) => {
  const singleFeature = useFeature(feature || '');
  const anyFeature = useAnyFeature(anyOf || []);
  const allFeatures = useAllFeatures(allOf || []);

  // Priority: single > anyOf > allOf > default true
  const isEnabled = feature
    ? singleFeature
    : anyOf
      ? anyFeature
      : allOf
        ? allFeatures
        : true;

  if (isEnabled) {
    return <>{children}</>;
  }
  if (fallback !== undefined) {
    return <>{fallback}</>;
  }
  if (!showDisabledMessage) {
    return null;
  }

  const requiredFeatures = feature
    ? [feature]
    : anyOf
      ? anyOf
      : allOf
        ? allOf
        : [];
  const requirementType: 'single' | 'any' | 'all' = feature
    ? 'single'
    : anyOf
      ? 'any'
      : 'all';

  const getMessage = () => {
    if (requiredFeatures.length === 0) {
      return 'Feature not available';
    }
    if (requiredFeatures.length === 1) {
      return 'This feature is currently disabled:';
    }
    switch (requirementType) {
      case 'any':
        return 'At least one of these features must be enabled:';
      case 'all':
        return 'All of these features must be enabled:';
      default:
        return 'This feature is currently disabled:';
    }
  };

  return (
    <Alert severity="info" data-testid="feature-message">
      <Text variant="body2" className="font-semibold">
        Feature Disabled
      </Text>
      <Text variant="body2" className="mb-2">
        {getMessage()}
      </Text>
      <Stack direction="row" spacing={1} className="flex-wrap">
        {requiredFeatures.map(f => (
          <Chip
            key={f}
            data-testid="feature-chip"
            label={f}
            size="sm"
            variant="outlined"
            className="font-mono text-sm"
          />
        ))}
      </Stack>
    </Alert>
  );
};
